import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, FileText, Upload, Camera, CheckCircle, AlertCircle, Truck, Shield, CreditCard, Calendar, QrCode, Link as LinkIcon, Clock } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';
import Icon from '../../components/AppIcon';


const DriverRegistrationProfile = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    fullName: '',
    dateOfBirth: '',
    email: '',
    countryCode: '+91',
    mobile: '',
    emergencyContact: '',
    emergencyName: '',
    
    // License Information
    licenseNumber: '',
    licenseExpiry: '',
    licenseState: '',
    licensePhoto: null,
    
    // Vehicle Association
    associationType: 'fleet', // 'fleet' or 'independent'
    fleetOwnerCode: '',
    ownVehicleNumber: '',
    vehicleType: '',
    
    // KYC Documents
    aadhaarNumber: '',
    panNumber: '',
    bankAccount: '',
    ifscCode: '',
    bankName: '',
    
    // Profile Photo
    profilePhoto: null
  });

  const [uploadedDocs, setUploadedDocs] = useState({
    license: false,
    aadhaar: false,
    pan: false,
    vehicle: false,
    profile: false
  });

  const [verificationStatus, setVerificationStatus] = useState({
    license: 'pending',
    aadhaar: 'pending',
    pan: 'pending',
    vehicle: 'pending'
  });

  const steps = [
    { id: 1, name: t('Personal Info'), icon: User },
    { id: 2, name: t('License Verification'), icon: FileText },
    { id: 3, name: t('Vehicle Association'), icon: Truck },
    { id: 4, name: t('KYC & Banking'), icon: CreditCard }
  ];

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = (type, file) => {
    if (file) {
      setFormData(prev => ({ ...prev, [type]: file }));
      setUploadedDocs(prev => ({ ...prev, [type]: true }));
      
      // Simulate OCR/verification
      setTimeout(() => {
        if (type === 'licensePhoto') {
          setVerificationStatus(prev => ({ ...prev, license: 'verified' }));
          // Auto-fill license details from OCR
          setFormData(prev => ({
            ...prev,
            licenseNumber: 'DL-123456789',
            licenseExpiry: '2027-12-31',
            licenseState: 'Gujarat'
          }));
        }
      }, 2000);
    }
  };

  const handleCameraCapture = (type) => {
    // Simulate camera capture
    alert(t('Camera would open here on mobile devices'));
    setUploadedDocs(prev => ({ ...prev, [type]: true }));
  };

  const handleFleetCodeScan = () => {
    // Simulate QR code scanning
    alert(t('QR scanner would open here'));
    setFormData(prev => ({ ...prev, fleetOwnerCode: 'FLT-2025-12345' }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final submission
      alert(t('Registration submitted successfully! Your profile is under verification.'));
      navigate('/driver-authentication');
    }
  };

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'active';
    return 'pending';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => navigate('/driver-authentication')}
              className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200"
            >
              ←
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{t('Driver Registration')}</h1>
              <p className="text-xs text-gray-600">{t('Complete your profile to start driving')}</p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </div>
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {steps?.map((step, index) => {
              const Icon = step?.icon;
              const status = getStepStatus(step?.id);
              return (
                <div key={step?.id} className="flex-1">
                  <div className="flex items-center">
                    <div className="flex flex-col items-center relative">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                        status === 'completed' ? 'bg-green-500 border-green-500' :
                        status === 'active'? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'
                      }`}>
                        {status === 'completed' ? (
                          <CheckCircle className="w-6 h-6 text-white" />
                        ) : (
                          <Icon className={`w-6 h-6 ${
                            status === 'active' ? 'text-white' : 'text-gray-400'
                          }`} />
                        )}
                      </div>
                      <p className={`mt-2 text-xs font-medium ${
                        status === 'active' ? 'text-blue-600' : 'text-gray-600'
                      }`}>
                        {step?.name}
                      </p>
                    </div>
                    {index < steps?.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-2 ${
                        status === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Personal Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('Personal Information')}
                  </h2>
                  <p className="text-gray-600">
                    {t('Provide your basic details and emergency contact')}
                  </p>
                </div>

                {/* Profile Photo */}
                <div className="flex items-center space-x-4 pb-6 border-b">
                  <div className="relative">
                    {uploadedDocs?.profile ? (
                      <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center">
                        <User className="w-12 h-12 text-blue-600" />
                      </div>
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border-2 border-dashed border-gray-300">
                        <User className="w-12 h-12 text-gray-400" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => handleCameraCapture('profile')}
                      className="absolute bottom-0 right-0 w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700"
                    >
                      <Camera className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('Profile Photo')}</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {t('Upload a clear photo for verification')}
                    </p>
                    <label className="inline-flex items-center px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition">
                      <Upload className="w-4 h-4 mr-2" />
                      <span className="text-sm font-medium">{t('Upload Photo')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleFileUpload('profilePhoto', e?.target?.files?.[0])}
                        className="hidden"
                      />
                    </label>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Full Name')} *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="text"
                        name="fullName"
                        value={formData?.fullName}
                        onChange={handleChange}
                        placeholder={t('Enter your full name')}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Date of Birth')} *
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData?.dateOfBirth}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Mobile Number')} *
                    </label>
                    <div className="flex space-x-2">
                      <select
                        name="countryCode"
                        value={formData?.countryCode}
                        onChange={handleChange}
                        className="w-24 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="+91">🇮🇳 +91</option>
                        <option value="+1">🇺🇸 +1</option>
                      </select>
                      <input
                        type="tel"
                        name="mobile"
                        value={formData?.mobile}
                        onChange={handleChange}
                        placeholder={t('10-digit number')}
                        className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength="10"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Email Address')}
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData?.email}
                        onChange={handleChange}
                        placeholder={t('your@email.com')}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="pt-6 border-t">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('Emergency Contact')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Contact Name')} *
                      </label>
                      <input
                        type="text"
                        name="emergencyName"
                        value={formData?.emergencyName}
                        onChange={handleChange}
                        placeholder={t('Emergency contact name')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Contact Number')} *
                      </label>
                      <input
                        type="tel"
                        name="emergencyContact"
                        value={formData?.emergencyContact}
                        onChange={handleChange}
                        placeholder={t('10-digit number')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        maxLength="10"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: License Verification */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('License Verification')}
                  </h2>
                  <p className="text-gray-600">
                    {t('Upload your driving license for verification with OCR scanning')}
                  </p>
                </div>

                {/* License Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  {uploadedDocs?.license ? (
                    <div className="space-y-4">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">{t('License Uploaded Successfully')}</p>
                        <p className="text-sm text-gray-600">{t('Scanning document...')}</p>
                      </div>
                      {verificationStatus?.license === 'verified' && (
                        <div className="inline-flex items-center px-4 py-2 bg-green-50 text-green-700 rounded-lg">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          {t('Verified')}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto" />
                      <div>
                        <h3 className="font-semibold text-gray-900 mb-2">
                          {t('Upload Driving License')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                          {t('Supported formats: JPG, PNG, PDF (Max 5MB)')}
                        </p>
                      </div>
                      <div className="flex justify-center space-x-3">
                        <button
                          type="button"
                          onClick={() => handleCameraCapture('license')}
                          className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                          <Camera className="w-5 h-5 mr-2" />
                          {t('Capture with Camera')}
                        </button>
                        <label className="inline-flex items-center px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer">
                          <Upload className="w-5 h-5 mr-2" />
                          {t('Upload File')}
                          <input
                            type="file"
                            accept="image/*,.pdf"
                            onChange={(e) => handleFileUpload('licensePhoto', e?.target?.files?.[0])}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                {/* License Details (Auto-filled by OCR) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('License Number')} *
                    </label>
                    <input
                      type="text"
                      name="licenseNumber"
                      value={formData?.licenseNumber}
                      onChange={handleChange}
                      placeholder={t('DL-1234567890')}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Expiry Date')} *
                    </label>
                    <input
                      type="date"
                      name="licenseExpiry"
                      value={formData?.licenseExpiry}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('Issuing State')} *
                    </label>
                    <select
                      name="licenseState"
                      value={formData?.licenseState}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">{t('Select State')}</option>
                      <option value="Gujarat">Gujarat</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Karnataka">Karnataka</option>
                    </select>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-blue-900 mb-1">{t('License Verification')}</p>
                    <p className="text-blue-700">
                      {t('Your license will be verified against government databases. Ensure all details match your original document.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Vehicle Association */}
            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('Vehicle Association')}
                  </h2>
                  <p className="text-gray-600">
                    {t('Link to a fleet owner or register as independent driver')}
                  </p>
                </div>

                {/* Association Type Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, associationType: 'fleet' }))}
                    className={`p-6 border-2 rounded-lg text-left transition ${
                      formData?.associationType === 'fleet' ?'border-blue-600 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        formData?.associationType === 'fleet' ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        <LinkIcon className={`w-6 h-6 ${
                          formData?.associationType === 'fleet' ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{t('Join Fleet Owner')}</h3>
                        <p className="text-sm text-gray-600">{t('Work with a transporter')}</p>
                      </div>
                    </div>
                    {formData?.associationType === 'fleet' && (
                      <div className="mt-3 text-sm text-blue-600">✓ {t('Selected')}</div>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, associationType: 'independent' }))}
                    className={`p-6 border-2 rounded-lg text-left transition ${
                      formData?.associationType === 'independent' ?'border-blue-600 bg-blue-50' :'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        formData?.associationType === 'independent' ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        <Truck className={`w-6 h-6 ${
                          formData?.associationType === 'independent' ? 'text-white' : 'text-gray-600'
                        }`} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{t('Independent Driver')}</h3>
                        <p className="text-sm text-gray-600">{t('Own your vehicle')}</p>
                      </div>
                    </div>
                    {formData?.associationType === 'independent' && (
                      <div className="mt-3 text-sm text-blue-600">✓ {t('Selected')}</div>
                    )}
                  </button>
                </div>

                {/* Fleet Owner Association */}
                {formData?.associationType === 'fleet' && (
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">
                        {t('Connect with Fleet Owner')}
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {t('Enter invitation code provided by your fleet owner or scan QR code')}
                      </p>
                      <div className="flex space-x-3">
                        <input
                          type="text"
                          name="fleetOwnerCode"
                          value={formData?.fleetOwnerCode}
                          onChange={handleChange}
                          placeholder={t('FLT-2025-XXXXX')}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={handleFleetCodeScan}
                          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
                        >
                          <QrCode className="w-5 h-5 mr-2" />
                          {t('Scan QR')}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Independent Vehicle Registration */}
                {formData?.associationType === 'independent' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('Vehicle Number')} *
                        </label>
                        <input
                          type="text"
                          name="ownVehicleNumber"
                          value={formData?.ownVehicleNumber}
                          onChange={handleChange}
                          placeholder={t('GJ-01-AB-1234')}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('Vehicle Type')} *
                        </label>
                        <select
                          name="vehicleType"
                          value={formData?.vehicleType}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          required
                        >
                          <option value="">{t('Select Type')}</option>
                          <option value="Truck">Truck</option>
                          <option value="Mini Truck">Mini Truck</option>
                          <option value="Trailer">Trailer</option>
                        </select>
                      </div>
                    </div>

                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <Truck className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-3">
                        {t('Upload vehicle RC & insurance documents')}
                      </p>
                      <label className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        {t('Upload Documents')}
                        <input
                          type="file"
                          multiple
                          accept="image/*,.pdf"
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                )}

                <div className="bg-yellow-50 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="font-medium text-yellow-900 mb-1">{t('GPS Tracking Required')}</p>
                    <p className="text-yellow-700">
                      {t('During active trips, you must enable GPS tracking on your mobile app. This allows transporters and buyers to track shipments in real-time.')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: KYC & Banking */}
            {currentStep === 4 && (
              <div className="space-y-6">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">
                    {t('KYC & Banking Details')}
                  </h2>
                  <p className="text-gray-600">
                    {t('Complete your verification and add bank account for payments')}
                  </p>
                </div>

                {/* Aadhaar Verification */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('Aadhaar Number')} *
                  </label>
                  <div className="flex space-x-3">
                    <input
                      type="text"
                      name="aadhaarNumber"
                      value={formData?.aadhaarNumber}
                      onChange={handleChange}
                      placeholder={t('1234 5678 9012')}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      maxLength="12"
                      required
                    />
                    <button
                      type="button"
                      className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap"
                    >
                      {t('Verify')}
                    </button>
                  </div>
                </div>

                {/* PAN Card */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('PAN Number')}
                  </label>
                  <input
                    type="text"
                    name="panNumber"
                    value={formData?.panNumber}
                    onChange={handleChange}
                    placeholder={t('ABCDE1234F')}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    maxLength="10"
                  />
                </div>

                {/* Banking Details */}
                <div className="pt-6 border-t">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('Bank Account Details')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Bank Name')} *
                      </label>
                      <input
                        type="text"
                        name="bankName"
                        value={formData?.bankName}
                        onChange={handleChange}
                        placeholder={t('Bank Name')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('Account Number')} *
                      </label>
                      <input
                        type="text"
                        name="bankAccount"
                        value={formData?.bankAccount}
                        onChange={handleChange}
                        placeholder={t('Account Number')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('IFSC Code')} *
                      </label>
                      <input
                        type="text"
                        name="ifscCode"
                        value={formData?.ifscCode}
                        onChange={handleChange}
                        placeholder={t('IFSC Code')}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        maxLength="11"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Verification Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">{t('Verification Status')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(verificationStatus)?.map(([key, status]) => (
                      <div key={key} className="text-center">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
                          status === 'verified' ? 'bg-green-500' : 'bg-gray-300'
                        }`}>
                          {status === 'verified' ? (
                            <CheckCircle className="w-6 h-6 text-white" />
                          ) : (
                            <Clock className="w-6 h-6 text-gray-600" />
                          )}
                        </div>
                        <p className="text-xs font-medium text-gray-700 capitalize">{t(key)}</p>
                        <p className="text-xs text-gray-600">{t(status)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-8 border-t mt-8">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
                >
                  {t('Previous')}
                </button>
              )}
              <div className={currentStep === 1 ? 'ml-auto' : ''}>
                <button
                  type="submit"
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold"
                >
                  {currentStep === 4 ? t('Submit Registration') : t('Continue')}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">{t('Need Help with Registration?')}</h3>
              <p className="text-sm text-gray-600 mb-3">
                {t('Our support team is available 24/7 to assist you with the registration process.')}
              </p>
              <div className="flex space-x-3">
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  {t('Chat with Support')}
                </button>
                <span className="text-gray-300">•</span>
                <button className="text-sm text-blue-600 hover:text-blue-700 font-medium">
                  {t('Call: 1800-XXX-XXXX')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverRegistrationProfile;